import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { getProducts } from "@/api";
import { MaskedLine, FadeIn, LotusMark } from "@/components/Reveal";
import Emblem from "@/components/Emblem";
import Marquee from "@/components/Marquee";
import ProductCard from "@/components/ProductCard";

let activeScrollFrame = null;
let autoScrollStop = null;

const stopScroll = () => {
  if (activeScrollFrame) {
    cancelAnimationFrame(activeScrollFrame);
    activeScrollFrame = null;
  }
  if (autoScrollStop) {
    autoScrollStop();
    autoScrollStop = null;
  }
};

const smoothScrollTo = (id) => {
  const target = document.getElementById(id);
  if (!target) return;

  stopScroll();

  const start = window.scrollY;
  const end = start + target.getBoundingClientRect().top - 16;
  const distance = end - start;
  const duration = 2000;
  const startTime = performance.now();
  const ease = (t) => 1 - Math.pow(1 - t, 3);

  const step = (now) => {
    const progress = Math.min((now - startTime) / duration, 1);
    window.scrollTo(0, start + distance * ease(progress));
    if (progress < 1) activeScrollFrame = requestAnimationFrame(step);
    else activeScrollFrame = null;
  };

  activeScrollFrame = requestAnimationFrame(step);
};

const startDiscoverScroll = () => {
  stopScroll();

  let stopped = false;
  let removeInterrupt = null;

  const stopAuto = () => {
    if (stopped) return;
    stopped = true;
    if (activeScrollFrame) {
      cancelAnimationFrame(activeScrollFrame);
      activeScrollFrame = null;
    }
    if (removeInterrupt) {
      removeInterrupt();
      removeInterrupt = null;
    }
    autoScrollStop = null;
  };

  autoScrollStop = stopAuto;

  const start = window.scrollY;
  const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
  const distance = Math.max(0, maxScroll - start);

  if (distance <= 1) {
    stopAuto();
    return;
  }

  // One continuous glide from the current position to the very bottom.
  // The duration scales with page length so the motion stays smooth rather
  // than pausing between artificial scroll segments.
  const duration = Math.max(5000, Math.min(14000, distance * 4.5));
  const startTime = performance.now();
  const ease = (t) => 1 - Math.pow(1 - t, 3);

  const step = (now) => {
    if (stopped) return;

    const progress = Math.min((now - startTime) / duration, 1);
    window.scrollTo(0, start + distance * ease(progress));

    if (progress < 1) {
      activeScrollFrame = requestAnimationFrame(step);
    } else {
      activeScrollFrame = null;
      stopAuto();
    }
  };

  // Ignore the initiating tap itself, then stop on the next user interaction.
  window.setTimeout(() => {
    if (stopped) return;
    const interrupt = () => stopAuto();
    window.addEventListener("pointerdown", interrupt, { once: true, passive: true });
    removeInterrupt = () => window.removeEventListener("pointerdown", interrupt);
  }, 0);

  activeScrollFrame = requestAnimationFrame(step);
};
const Home = () => {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    getProducts({ featured: true }).then(setFeatured).catch(() => {});
  }, []);

  return (
    <>
      <style>{`
@keyframes vaiyraScroll {
  0%, 100% { transform: translateY(0); opacity: .55; }
  50% { transform: translateY(5px); opacity: 1; }
}
html { scroll-behavior: auto; }
`}</style>
      <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[74svh] sm:min-h-[88vh] flex items-center overflow-hidden pt-16 sm:pt-20 pb-8 sm:pb-12">
        <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-gold/15 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-[420px] h-[420px] rounded-full bg-maroon/10 blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-5 sm:px-12 lg:px-20 w-full grid grid-cols-1 lg:grid-cols-12 gap-3 sm:gap-8 lg:gap-16 items-center py-4 sm:py-16">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-[10px] sm:text-xs uppercase tracking-[0.35em] sm:tracking-[0.45em] text-gold-dark flex items-center gap-3 sm:gap-4"
            >
              <span className="w-7 sm:w-10 h-px bg-gold inline-block" /> The Debut Collection · 2026
            </motion.p>

            <h1 className="mt-4 sm:mt-8 font-display text-maroon-deep text-[2.9rem] sm:text-7xl lg:text-8xl leading-[0.93] tracking-tight">
              <MaskedLine delay={0.35}>Rooted</MaskedLine>
              <MaskedLine delay={0.5}>
                in <em className="text-gold-dark not-italic font-display italic">Grace.</em>
              </MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.85 }}
              className="mt-4 sm:mt-8 max-w-md text-[13px] sm:text-lg text-clay leading-[1.55] sm:leading-relaxed"
            >
              Vaiyra is a new Indian womenswear house — short kurtas, long kurtas and cord sets, woven from pure cloth and finished by hand. Heritage you can wear every day.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.05 }}
              className="mt-5 sm:mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-4"
            >
              <a
                href="#signature-pieces"
                onClick={(e) => {
                  e.preventDefault();
                  smoothScrollTo("signature-pieces");
                }}
                data-testid="hero-cta-explore"
                className="group inline-flex w-full sm:w-auto items-center justify-center gap-3 bg-maroon text-ivory px-7 py-3.5 text-[10px] sm:text-xs tracking-[0.3em] sm:tracking-[0.35em] uppercase hover:bg-maroon-hover transition-colors duration-300"
              >
                Explore Collection
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5" />
              </a>
              <a
                href="#signature-pieces"
                onClick={(e) => {
                  e.preventDefault();
                  startDiscoverScroll();
                }}
                className="group inline-flex items-center justify-center gap-2 py-2 text-[9px] tracking-[0.3em] uppercase text-clay hover:text-maroon-deep transition-colors"
                aria-label="Scroll to discover"
              >
                <ArrowDown className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-y-1" />
                Scroll to discover
              </a>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5 flex justify-center lg:justify-end scale-[0.9] sm:scale-100 mt-0 sm:mt-0"
          >
            <Emblem />
          </motion.div>
        </div>
      </section>

      {/* FEATURED COLLECTION */}
      <section id="signature-pieces" data-testid="featured-collection" className="scroll-mt-16 py-12 sm:py-32 bg-ivory-deep/60 border-y border-gold/20">
        <div className="max-w-7xl mx-auto px-5 sm:px-12 lg:px-20">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <FadeIn>
              <p className="text-xs uppercase tracking-[0.45em] text-gold-dark">From the Atelier</p>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl text-maroon-deep">The Signature Pieces</h2>
            </FadeIn>
            <FadeIn delay={0.1}>
              <Link
                to="/shop"
                data-testid="featured-view-all-link"
                className="group inline-flex items-center gap-3 text-xs tracking-[0.35em] uppercase text-maroon-deep border-b border-gold pb-1 hover:text-gold-dark transition-colors"
              >
                View the full collection
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5" />
              </Link>
            </FadeIn>
          </div>

          <div className="mt-9 sm:mt-14 grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8 lg:gap-10">
            {featured.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      <Marquee />

      {/* CLOSING NOTE */}
      <section className="py-24 sm:py-32">
        <FadeIn className="max-w-3xl mx-auto px-6 text-center">
          <LotusMark className="w-10 h-10 text-gold mx-auto" />
          <p className="mt-8 font-display italic text-3xl sm:text-4xl lg:text-5xl text-maroon-deep leading-snug text-balance">
            "For you with Grace — thank you for making Vaiyra a part of your wardrobe."
          </p>
          <Link
            to="/shop"
            data-testid="closing-cta-shop"
            className="mt-10 inline-flex items-center gap-3 bg-maroon text-ivory px-8 py-4 text-xs tracking-[0.35em] uppercase hover:bg-maroon-hover transition-colors duration-300"
          >
            Begin Your Wardrobe <ArrowRight className="w-4 h-4" />
          </Link>
        </FadeIn>
      </section>
      </div>
    </>
  );
};

export default Home;
